import React, { ReactNode, useState } from 'react';
import { ExpandMore, TimerSharp } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CardActionArea,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Stage, TaskItem } from '../../taskList/interfaces';
import { FORMS, TaskItemStatus } from '../constants/constants';
import { SessionStore } from '../../constants';
import { BasicTaskList } from '../styles/BasicTaskList';
import BasicProgressBar from './BasicProgressBar';
import { initializeTask } from '../../taskList/utils/initializeTask';
import { ReactComponent as LockTileIcon } from '../images/lock_icon.svg';
import { ReactComponent as CompletedTaskIcon } from '../../taskList/images/completed_task.svg';
import { formsIndicators } from '../utils/checkFormsCompleted';
import { Alert } from '@material-ui/lab';
import { useAppSelector } from '../../redux-store/hook';
import { ResponseReuseType } from '../../taskList/constants';

interface StageSectionProps {
  data: Stage[];
  onRRMsgBtnClick?: (showPopup: boolean, currentItem?: TaskItem) => void;
}
const StageSection = (props: StageSectionProps): JSX.Element => {
  const { data, onRRMsgBtnClick } = props;
  const { settings } = useAppSelector((state) => state.template);
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const candidate = useAppSelector((state) => state.candidate);
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const basicTaskListHurdleIndicator = sessionStorage.getItem(SessionStore.basicTaskListHurdleIndicator) === 'true';
  const [alertVisible, setAlertVisible] = useState<boolean>(basicTaskListHurdleIndicator);
  const formStatusSession = sessionStorage.getItem(SessionStore.formStatus);
  const formStatus = formStatusSession && JSON.parse(formStatusSession);
  const indicatorObj = formsIndicators();
  const classes = BasicTaskList({ isICPEnabled: isICPEnabled, settings: settings });

  const onStageItemClick = async (item: TaskItem, stage: Stage): Promise<void> => {
    if (item.code === FORMS) {
      history.push(`${item.link}`);
    } else {
      await initializeTask(stage, userSessionActive, history, location, candidate, null, item, currentWorkflowItem);
    }
  };

  const handleAlertHide = (): void => {
    sessionStorage.setItem(SessionStore.basicTaskListHurdleIndicator, 'false');
    setAlertVisible(false);
  };

  const checkStart = (item: TaskItem, stage: Stage): ReactNode => {
    const hasExhausted = item.state && JSON.parse(item.state)?.hasExhausted;
    if (
      (item.completionIndicator && formStatus && formStatus[item.completionIndicator]) ||
      item.status === TaskItemStatus.COMPLETED
    ) {
      return <CompletedTaskIcon className={`${classes.completedIcon} ${classes.floatRight}`} />;
    } else if ((item.isActive || !stage.isSequentialFlow) && item.status === TaskItemStatus.IN_PROGRESS) {
      return (
        <div title={hasExhausted ? t('exitEntryLimitExhaust') : ''}>
          <Button
            className={classes.fullLengthButton}
            disabled={hasExhausted}
            variant="outlined"
            color="primary"
            onClick={(): Promise<void> => onStageItemClick(item, stage)}>
            {t('continue')}
          </Button>
        </div>
      );
    } else if (
      (item.code === FORMS &&
        item.completionIndicator &&
        formStatus &&
        !formStatus[item.completionIndicator] &&
        item.isActive &&
        item.link) ||
      (item.code !== FORMS && item.status === TaskItemStatus.NOT_STARTED && (item.isActive || !stage.isSequentialFlow))
    ) {
      return (
        <Button
          className={classes.fullLengthButton}
          variant="outlined"
          color="primary"
          onClick={(): Promise<void> => onStageItemClick(item, stage)}>
          {t('taskItemStartButtonText')}
        </Button>
      );
    } else if (stage.isSequentialFlow || item.code === FORMS) {
      return (
        <Box
          className={`${classes.floatRight}`}
          title={t('taskTileLockedStatus')}
          aria-label={t('taskTileLockedStatus')}>
          <LockTileIcon className={`${classes.lock}`} />
        </Box>
      );
    } else return null;
  };

  const hurdleIndicatorIndex: number =
    data.length && data[0].title && data[0].title.toString().toUpperCase() === FORMS ? 1 : 0;
  const isHurdled: boolean = data.length > 0 && data.filter((stage) => stage.hurdleType != null).length > 0;

  const projectSettingsString = sessionStorage.getItem(SessionStore.projectSettings);
  const showSubsequentStages = projectSettingsString && JSON.parse(projectSettingsString).showSubsequentStages;

  return (
    <>
      <Box className={classes.accordionWrapper}>
        {data.map((stage, index: number) => (
          <>
            {(stage.visibility || stage.title.toString().toUpperCase() === FORMS) && (
              <>
                {index === hurdleIndicatorIndex && isHurdled && (
                  <Alert
                    severity="error"
                    onClose={(): void => {
                      handleAlertHide();
                    }}
                    className={`${classes.alertBox} ${!alertVisible && classes.hideAlert}`}>
                    {t('basicTaskListAlert', {
                      taskCount: showSubsequentStages ? data.length - hurdleIndicatorIndex : 'n',
                    })}
                  </Alert>
                )}
                <Accordion
                  key={stage.title}
                  variant="outlined"
                  className={`${classes.accordion} ${
                    stage.completed || stage.locked ? classes.stageDisabledColor : ''
                  }`}
                  disabled={stage.locked}
                  defaultExpanded={stage.isActive}>
                  <AccordionSummary
                    expandIcon={stage.locked ? <LockTileIcon className={`${classes.lock}`} /> : <ExpandMore />}
                    className={`${classes.accordionSummary} ${classes.positionRelative}`}>
                    <Box flex={1} className={classes.width100}>
                      <span className={classes.cardTitle}>
                        {stage.title.toString().toUpperCase() === FORMS ? t(stage.title).toString() : stage.title}
                      </span>
                      <BasicProgressBar data={stage.items} />
                    </Box>
                    <Box flex={1} className={`${classes.width100} ${classes.textAlignRight}`}>
                      {!stage.completed ? (
                        stage.title.toString().toUpperCase() !== FORMS && (
                          <>
                            <TimerSharp className={classes.timerIcon} />
                            <span className={classes.timeValue}>{stage.duration}</span>
                            <span className={classes.description}> {t('minutes')}</span>
                          </>
                        )
                      ) : (
                        <>
                          <CompletedTaskIcon className={`${classes.completedIcon} ${classes.verticalAlignTop}`} />
                          <span className={classes.completedTitle}>{t('taskTileCompletedStatus')}</span>
                        </>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {stage.items.map((item: TaskItem) =>
                      (item.code === FORMS && item.indicator && indicatorObj[item.indicator]) || item.code !== FORMS ? (
                        <Paper key={item.name} square={false} className={classes.card} variant="outlined">
                          <CardActionArea
                            component="div"
                            classes={{
                              root: classes.actionArea,
                              focusHighlight: classes.focusHighlight,
                            }}>
                            {/* // onMouseEnter={(): void => updateStartButton(item, stage, false)}
                      // onMouseLeave={(): void => updateStartButton(item, stage, true)}> */}
                            {smUp ? (
                              <Box display="flex" alignItems="center" justifyContent="center">
                                {item.code !== FORMS && (
                                  <Box flex="15%" className={classes.timerText}>
                                    <span className={classes.timeValue}>{item.duration}</span>
                                    <span className={classes.description}> {t('minutes')}</span>
                                  </Box>
                                )}

                                <Box flex="75%" className={item.code !== FORMS ? classes.cardContent : ''}>
                                  <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                  <Typography className={classes.description}>{item.description}</Typography>
                                  {item?.responseReuseType === ResponseReuseType.FULL_RR && (
                                    <Typography
                                      variant="body2"
                                      color="inherit"
                                      data-testid={'rr-label'}
                                      className={classes.reponseReuseLabel}
                                      onClick={(): void => onRRMsgBtnClick && onRRMsgBtnClick(true, item)}>
                                      <strong>{t('rrLabel')}</strong>
                                    </Typography>
                                  )}
                                </Box>
                                <Box flex="10%">{checkStart(item, stage)}</Box>
                              </Box>
                            ) : (
                              <>
                                {item.code !== FORMS && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    className={`${classes.accordionContentPaddingMobile} ${classes.cardContentMobile}`}>
                                    <Box flex="50%" className={classes.timerText}>
                                      <span className={classes.timeValue}>{item.duration}</span>
                                      <span className={classes.description}> {t('minutes')}</span>
                                    </Box>
                                    {item?.responseReuseType === ResponseReuseType.FULL_RR && (
                                      <Typography
                                        className={classes.reponseReuseLabel}
                                        variant="body2"
                                        data-testid={'rr-label'}
                                        color="inherit"
                                        onClick={(): void => onRRMsgBtnClick && onRRMsgBtnClick(true, item)}>
                                        <strong>{t('rrLabel')}</strong>
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                {(!item.isActive && (stage.isSequentialFlow || item.code === FORMS)) ||
                                (!stage.isSequentialFlow && item.status === TaskItemStatus.COMPLETED) ? (
                                  <Box display="flex" alignItems="center" justifyContent="center">
                                    <Box flex="75%" className={`${classes.accordionContentPaddingMobile}`}>
                                      <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                      <Typography variant="subtitle2">{item.description}</Typography>
                                    </Box>
                                    <Box>{checkStart(item, stage)}</Box>
                                  </Box>
                                ) : (
                                  <Box className={`${classes.accordionContentPaddingMobile}`}>
                                    <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                    <Typography className={classes.description}>{item.description}</Typography>
                                  </Box>
                                )}

                                {(item.isActive && stage.isSequentialFlow) ||
                                (item.isActive && item.code === FORMS) ||
                                (!stage.isSequentialFlow &&
                                  (item.status === TaskItemStatus.NOT_STARTED ||
                                    item.status === TaskItemStatus.IN_PROGRESS) &&
                                  item.code !== FORMS) ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    className={classes.accordionContentPaddingMobile}>
                                    <Box flex="50%" className={classes.alignRight}>
                                      {checkStart(item, stage)}
                                    </Box>
                                  </Box>
                                ) : (
                                  <></>
                                )}
                              </>
                            )}
                          </CardActionArea>
                        </Paper>
                      ) : (
                        ''
                      ),
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </>
        ))}
      </Box>
    </>
  );
};

export default StageSection;
